/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

const allowedJsTypes = new Set(["boolean", "number", "string"] as const)

/**
 * normalizes the facet object that is going to be passed into GrabX.
 * this cleans up non-primitive values (i.e. objects) and ensures that it is stringified before the event is sent.
 */
export default function normalizeFacets(
  facets: object,
): Record<string, string | number | boolean | null | undefined> {
  const normalizedFacetsEntries = Object.entries(facets).map<
    [string, string | number | boolean | null | undefined]
  >(([k, v]) => {
    if (typeof v === "bigint") {
      // bigint cannot be JSON-stringified, so just return a string (assumption: you don't need to do any arithmetics)
      return [k, v.toString()]
    }
    if (v === null || v === undefined) {
      // remove null / undefined values
      return [k, undefined]
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (allowedJsTypes.has(typeof v as any)) {
      return [k, v]
    }
    // convert into string if it's array of string / number
    const isStringsArray =
      Array.isArray(v) &&
      v.every(item => typeof item === "string" || typeof item === "number")
    if (isStringsArray) {
      return [k, v.toString()]
    }
    // doing JSON.stringify for now without sending Sentry Error.
    const normalizedVal =
      v instanceof Error ? `Error: ${v.message}` : JSON.stringify(v)
    return [k, normalizedVal]
  })
  // build facet object
  const normalizedFacets: Record<
    string,
    string | number | boolean | null | undefined
  > = {}
  normalizedFacetsEntries.forEach(([k, v]) => {
    if (v === undefined || v === null) {
      return
    }
    normalizedFacets[k] = v
  })
  return normalizedFacets
}
